import { supabase } from './client'
import { fileToProfileImageBase64 } from '@lib/constants/profileImageUpload'

// ── Interfaces ──────────────────────────────────────────────────────────────

export interface ProviderAppointmentItem {
  id: string
  conversationId?: string | null
  patientId: string
  patientName: string
  patientPhone: string
  patientImage?: string
  serviceType: string
  status: string
  scheduledDate: string
  scheduledTime?: string
  location: string
  notes?: string
  totalCost: number
  isContinuation: boolean
  createdAt: string
}

export interface ProviderAppointmentDetail {
  id: string
  conversationId?: string | null
  patientId: string
  patientName: string
  patientPhone: string
  patientEmail?: string
  patientImage?: string
  patientAge?: number
  patientGender?: string
  providerId: string
  serviceType: string
  duration: string
  numberOfDays?: number
  numberOfPatients?: number
  urgencyLevel?: string
  status: string
  scheduledDate: string
  scheduledTime?: string
  location: string
  notes?: string
  continuationOfAppointmentId?: string
  isContinuation: boolean
  totalCost: number
  createdAt: string
  medicalInfo?: {
    currentSymptoms?: string
    medicalHistory?: string
    allergies?: string
    currentMedications?: string
    surgeryHistory?: string
    familyHistory?: string
    accidentRecord?: string
    bloodType?: string
    heightCm?: number
    weightKg?: number
  }
  emergencyContacts?: Array<{
    id: string
    name: string
    relationship: string
    phoneNumber: string
  }>
}

export interface ProviderStats {
  todayCount: number
  pendingCount: number
  weekEarnings: number
  rating: number
  ratingCount: number
}

export interface EarningsRecord {
  appointmentId: string
  patientName: string
  serviceType: string
  amount: number
  date: string
  completedAt?: string
}

type ProviderCreditLogRow = {
  related_appointment_id: string | null
  amount: number | string | null
  platform_fee_amount: number | string | null
  created_at: string | null
  description: string | null
}

export interface EarningsSummary {
  totalEarned: number
  platformFee: number
  netCredited: number
  transactionCount: number
}

export interface WalletTransaction {
  id: string
  type: 'earning' | 'withdrawal'
  amount: number
  platformFee: number
  description: string
  status?: string
  createdAt: string
  withdrawalId?: string
}

type ProviderWithdrawalRow = {
  id: string
  amount: number | string | null
  status: string | null
  requested_at: string | null
}

// ── Internal helpers ─────────────────────────────────────────────────────────

const APPOINTMENT_SELECT = `
  id,
  conversation_id,
  provider_id,
  patient_id,
  service_type,
  status,
  scheduled_date,
  scheduled_time,
  location,
  notes,
  continuation_of_appointment_id,
  total_cost,
  created_at,
  updated_at,
  patients (
    id,
    name,
    phone_number,
    profile_image_url
  )
`

function mapRow(row: any): ProviderAppointmentItem {
  return {
    id: row.id,
    conversationId: row.conversation_id ?? null,
    patientId: row.patient_id,
    patientName: (row.patients as any)?.name ?? '',
    patientPhone: (row.patients as any)?.phone_number ?? '',
    patientImage: (row.patients as any)?.profile_image_url ?? undefined,
    serviceType: row.service_type,
    status: row.status ?? 'Requested',
    scheduledDate: row.scheduled_date ?? '',
    scheduledTime: row.scheduled_time ?? undefined,
    location: row.location ?? '',
    notes: row.notes ?? undefined,
    totalCost: row.total_cost ?? 0,
    isContinuation: Boolean(row.continuation_of_appointment_id),
    createdAt: row.created_at ?? '',
  }
}

function collapseChatContinuations(appointments: ProviderAppointmentItem[]): ProviderAppointmentItem[] {
  const latestByConversation = new Map<string, ProviderAppointmentItem>()
  const nonChat: ProviderAppointmentItem[] = []

  for (const appointment of appointments) {
    if (appointment.serviceType !== 'Chat Consultation') {
      nonChat.push(appointment)
      continue
    }

    const key = appointment.conversationId || appointment.id
    const existing = latestByConversation.get(key)
    if (!existing || (appointment.createdAt || '') > (existing.createdAt || '')) {
      latestByConversation.set(key, appointment)
    }
  }

  return [...nonChat, ...Array.from(latestByConversation.values())].sort((left, right) =>
    (right.createdAt || '').localeCompare(left.createdAt || '')
  )
}

async function getProviderId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) return null
  const { data } = await supabase
    .from('providers')
    .select('id')
    .eq('auth_id', user.id)
    .single()
  return (data as any)?.id ?? null
}

// ── Service ──────────────────────────────────────────────────────────────────

export const providerDashboardService = {
  // Expose for external use
  async getProviderId(): Promise<string | null> {
    return getProviderId()
  },

  /** All appointments for the provider, optionally filtered by status */
  async getAppointments(status?: string): Promise<ProviderAppointmentItem[]> {
    const providerId = await getProviderId()
    if (!providerId) return []

    let query = supabase
      .from('appointments')
      .select(APPOINTMENT_SELECT)
      .eq('provider_id', providerId)
      .order('scheduled_date', { ascending: false })
      .order('created_at', { ascending: false })

    if (status) query = (query as any).eq('status', status)

    const { data, error } = await query
    if (error) { console.error(error); return [] }
    return ((data as any[]) ?? []).map(mapRow)
  },

  async getAppointmentsPage(
    status?: string,
    limit = 8,
    offset = 0,
    excludeServiceType?: string,
  ): Promise<{ appointments: ProviderAppointmentItem[]; hasMore: boolean; nextOffset: number | null }> {
    const providerId = await getProviderId()
    if (!providerId) return { appointments: [], hasMore: false, nextOffset: null }

    let query = supabase
      .from('appointments')
      .select(APPOINTMENT_SELECT)
      .eq('provider_id', providerId)
      .order('scheduled_date', { ascending: false })
      .order('created_at', { ascending: false })
      .range(offset, offset + limit)

  if (status) query = (query as any).eq('status', status)
  if (excludeServiceType) query = (query as any).neq('service_type', excludeServiceType)

    const { data, error } = await query
    if (error) {
      console.error(error)
      return { appointments: [], hasMore: false, nextOffset: null }
    }

    const rows = ((data as any[]) ?? [])
    const hasMore = rows.length > limit
    const appointments = rows.slice(0, limit).map(mapRow)

    return {
      appointments,
      hasMore,
      nextOffset: hasMore ? offset + limit : null,
    }
  },

  /** Upcoming appointments (Requested|Scheduled, from today) */
  async getUpcomingAppointments(limit = 5, excludeServiceType?: string): Promise<ProviderAppointmentItem[]> {
    const providerId = await getProviderId()
    if (!providerId) return []

    const today = new Date().toISOString().split('T')[0]
    let query = supabase
      .from('appointments')
      .select(APPOINTMENT_SELECT)
      .eq('provider_id', providerId)
      .gte('scheduled_date', today)
      .in('status', ['Requested', 'Scheduled'])
      .order('scheduled_date', { ascending: true })
      .limit(limit)

    if (excludeServiceType) query = (query as any).neq('service_type', excludeServiceType)

    const { data, error } = await query

    if (error) { console.error(error); return [] }
    return ((data as any[]) ?? []).map(mapRow)
  },

  async getChatAppointments(): Promise<ProviderAppointmentItem[]> {
    const providerId = await getProviderId()
    if (!providerId) return []

    const { data, error } = await supabase
      .from('appointments')
      .select(APPOINTMENT_SELECT)
      .eq('provider_id', providerId)
      .eq('service_type', 'Chat Consultation')
      .order('created_at', { ascending: false })

    if (error) {
      console.error(error)
      return []
    }

    return collapseChatContinuations(((data as any[]) ?? []).map(mapRow))
  },

  /** Dashboard stats */
  async getStats(): Promise<ProviderStats> {
    const providerId = await getProviderId()
    if (!providerId) {
      return { todayCount: 0, pendingCount: 0, weekEarnings: 0, rating: 0, ratingCount: 0 }
    }

    const today = new Date()
    const todayStr = today.toISOString().split('T')[0]
    const weekStart = new Date(today)
    weekStart.setDate(today.getDate() - today.getDay())
    const weekStartStr = weekStart.toISOString().split('T')[0]

    const weekEndExclusive = new Date(today)
    weekEndExclusive.setDate(today.getDate() + 1)

    const [todayRes, pendingRes, earningsRes, ratingRes] = await Promise.all([
      supabase
        .from('appointments')
        .select('id', { count: 'exact', head: true })
        .eq('provider_id', providerId)
        .neq('service_type', 'Chat Consultation')
        .eq('scheduled_date', todayStr)
        .in('status', ['Requested', 'Scheduled']),
      supabase
        .from('appointments')
        .select('id', { count: 'exact', head: true })
        .eq('provider_id', providerId)
        .neq('service_type', 'Chat Consultation')
        .eq('status', 'Requested'),
      supabase
        .from('provider_transaction_logs')
        .select('amount, platform_fee_amount')
        .eq('provider_id', providerId)
        .eq('transaction_type', 'credit')
        .gte('created_at', `${weekStartStr}T00:00:00.000Z`)
        .lt('created_at', weekEndExclusive.toISOString()),
      supabase
        .from('reviews')
        .select('rating')
        .eq('provider_id', providerId),
    ])

    const weekEarnings = ((earningsRes.data as any[]) ?? []).reduce(
      (sum: number, r: any) => sum + Number(r.amount ?? 0) + Number(r.platform_fee_amount ?? 0),
      0
    )
    const ratings = ((ratingRes.data as any[]) ?? [])
      .map((r: any) => r.rating as number)
      .filter(Boolean)
    const rating =
      ratings.length > 0
        ? ratings.reduce((a, b) => a + b, 0) / ratings.length
        : 0

    return {
      todayCount: todayRes.count ?? 0,
      pendingCount: pendingRes.count ?? 0,
      weekEarnings,
      rating: Math.round(rating * 10) / 10,
      ratingCount: ratings.length,
    }
  },

  /** Earnings history filtered by optional date range */
  async getEarningsHistory(startDate?: string, endDate?: string): Promise<EarningsRecord[]> {
    const providerId = await getProviderId()
    if (!providerId) return []

    let query = supabase
      .from('provider_transaction_logs')
      .select('related_appointment_id, amount, platform_fee_amount, created_at, description')
      .eq('provider_id', providerId)
      .eq('transaction_type', 'credit')
      .order('created_at', { ascending: false })

    if (startDate) query = (query as any).gte('created_at', `${startDate}T00:00:00.000Z`)
    if (endDate) query = (query as any).lte('created_at', `${endDate}T23:59:59.999Z`)

    const { data, error } = await query
    if (error) { console.error(error); return [] }

    const logs = ((data as ProviderCreditLogRow[]) ?? [])
    const appointmentIds = Array.from(new Set(logs.map((log) => log.related_appointment_id).filter(Boolean) as string[]))

    let appointmentDetails = new Map<string, { patientName: string; serviceType: string }>()

    if (appointmentIds.length > 0) {
      const { data: appointments, error: appointmentError } = await supabase
        .from('appointments')
        .select('id, service_type, patients ( name )')
        .in('id', appointmentIds)

      if (appointmentError) {
        console.error(appointmentError)
      } else {
        appointmentDetails = new Map(
          ((appointments as any[]) ?? []).map((appointment) => [
            appointment.id as string,
            {
              patientName: (appointment.patients as any)?.name ?? 'Unknown Patient',
              serviceType: (appointment.service_type as string) ?? 'Appointment',
            },
          ])
        )
      }
    }

    return logs.map((log, index) => {
      const appointmentId = log.related_appointment_id ?? `credit-${index}`
      const details = log.related_appointment_id ? appointmentDetails.get(log.related_appointment_id) : undefined

      return {
        appointmentId,
        patientName: details?.patientName ?? 'Wallet Credit',
        serviceType: details?.serviceType ?? (log.description ?? 'Completed appointment'),
        amount: Number(log.amount ?? 0) + Number(log.platform_fee_amount ?? 0),
        date: log.created_at ?? '',
        completedAt: log.created_at ?? undefined,
      }
    })
  },

  /** Accept, decline (cancel), or complete an appointment */
  async updateStatus(
    appointmentId: string,
    status: string,
    reason?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      if (status === 'Cancelled') {
        const { error } = await supabase.rpc('cancel_appointment', {
          appointment_id_param: appointmentId,
          cancellation_reason_param: reason ?? null,
        })
        if (error) throw error
        return { success: true }
      }
      const { error } = await supabase
        .from('appointments')
        .update({
          status,
          ...(status === 'Completed' && { completed_at: new Date().toISOString() }),
          updated_at: new Date().toISOString(),
        })
        .eq('id', appointmentId)
      if (error) throw error
      return { success: true }
    } catch (err: any) {
      return { success: false, error: err.message as string }
    }
  },

  /** Update editable provider profile fields */
  async updateProfile(updates: {
    name?: string
    phone_number?: string
    specialty?: string
    experience?: string
    gender?: string
    date_of_birth?: string | null
    address?: string
    qualification?: string
    languages?: string[]
    specializations?: string[]
    bio?: string
  }): Promise<{ success: boolean; error?: string }> {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) return { success: false, error: 'Not authenticated' }

    const { error } = await supabase
      .from('providers')
      .update({ ...updates })
      .eq('auth_id', user.id)

    if (error) return { success: false, error: error.message }
    return { success: true }
  },

  /** Upload provider profile photo and update profile_image_url */
  async uploadProfileImage(file: File): Promise<{ success: boolean; url?: string; error?: string }> {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) return { success: false, error: 'Not authenticated' }

    try {
      const { base64, mime } = await fileToProfileImageBase64(file)
      const { data, error } = await supabase.functions.invoke('upload-profile-image', {
        body: {
          role: 'provider',
          contentType: mime,
          imageBase64: base64,
        },
      })

      if (error) return { success: false, error: error.message }
      if (!data?.success) return { success: false, error: data?.error ?? 'Failed to upload profile image.' }

      const url = data.url || data.imageUrl
      if (!url) return { success: false, error: 'Failed to generate public URL.' }

      return { success: true, url }
    } catch (error: any) {
      return { success: false, error: error.message ?? 'Failed to upload profile image.' }
    }
  },

  // ── Service Areas ────────────────────────────────────────────────────────

  async getServiceAreas(providerId: string) {
    const { data, error } = await supabase
      .from('provider_service_areas')
      .select('*')
      .eq('provider_id', providerId)
      .order('state')
    if (error) return []
    return data ?? []
  },

  async updateServiceArea(areaId: string, updates: { is_active?: boolean; travel_radius?: number }): Promise<{ success: boolean; error?: string }> {
    const { error } = await supabase
      .from('provider_service_areas')
      .update(updates)
      .eq('id', areaId)
    if (error) return { success: false, error: error.message }
    return { success: true }
  },

  async createServiceArea(
    providerId: string,
    serviceArea: { country: string; state: string; travel_radius?: number; is_active?: boolean }
  ): Promise<{ success: boolean; error?: string }> {
    const { data: existing } = await supabase
      .from('provider_service_areas')
      .select('id')
      .eq('provider_id', providerId)
      .eq('country', serviceArea.country)
      .eq('state', serviceArea.state)
      .maybeSingle()

    if (existing) return { success: true }

    const { error } = await supabase
      .from('provider_service_areas')
      .insert({
        provider_id: providerId,
        country: serviceArea.country,
        state: serviceArea.state,
        travel_radius: serviceArea.travel_radius ?? 10,
        is_active: serviceArea.is_active ?? false,
      })

    if (error) return { success: false, error: error.message }
    return { success: true }
  },

  // ── Availability ─────────────────────────────────────────────────────────

  async getAvailability(providerId: string) {
    const { data, error } = await supabase
      .from('provider_availability')
      .select('*, provider_time_slots(*)')
      .eq('provider_id', providerId)
      .order('day_of_week')
    if (error) return []
    return data ?? []
  },

  async upsertDayAvailability(providerId: string, day: string, isAvailable: boolean): Promise<{ success: boolean; error?: string; id?: string }> {
    const { data: existing } = await supabase
      .from('provider_availability')
      .select('id')
      .eq('provider_id', providerId)
      .eq('day_of_week', day)
      .maybeSingle()

    if (existing) {
      const { error } = await supabase
        .from('provider_availability')
        .update({ is_available: isAvailable })
        .eq('id', existing.id)
      return error ? { success: false, error: error.message } : { success: true, id: existing.id }
    }

    const { data, error } = await supabase
      .from('provider_availability')
      .insert({ provider_id: providerId, day_of_week: day, is_available: isAvailable })
      .select('id')
      .single()
    return error ? { success: false, error: error.message } : { success: true, id: data.id }
  },

  async addTimeSlot(availabilityId: string, startTime: string, endTime: string): Promise<{ success: boolean; error?: string; id?: string }> {
    const { data, error } = await supabase
      .from('provider_time_slots')
      .insert({ availability_id: availabilityId, start_time: startTime, end_time: endTime })
      .select('id')
      .single()
    return error ? { success: false, error: error.message } : { success: true, id: data.id }
  },

  async deleteTimeSlot(slotId: string): Promise<{ success: boolean; error?: string }> {
    const { error } = await supabase.from('provider_time_slots').delete().eq('id', slotId)
    return error ? { success: false, error: error.message } : { success: true }
  },

  // ── Payout & Wallet ───────────────────────────────────────────────────────

  async getWallet(providerId: string) {
    const { data } = await supabase
      .from('provider_wallets')
      .select('*')
      .eq('provider_id', providerId)
      .maybeSingle()
    return data ?? null
  },

  async getPayoutMethods(providerId: string) {
    const { data } = await supabase
      .from('provider_payout_methods')
      .select('*')
      .eq('provider_id', providerId)
      .order('created_at', { ascending: false })
    return data ?? []
  },

  async addPayoutMethod(providerId: string, method: {
    method_type: 'bank_account'
    account_name: string
    account_number: string
    bank_name: string
    bank_code?: string
  }): Promise<{ success: boolean; error?: string }> {
    const { error } = await supabase
      .from('provider_payout_methods')
      .insert({ provider_id: providerId, ...method })
    return error ? { success: false, error: error.message } : { success: true }
  },

  async updatePayoutMethod(id: string, providerId: string, updates: {
    account_name?: string
    account_number?: string
    bank_name?: string
    bank_code?: string
  }): Promise<{ success: boolean; error?: string }> {
    const { error } = await supabase
      .from('provider_payout_methods')
      .update(updates)
      .eq('id', id)
      .eq('provider_id', providerId)
    return error ? { success: false, error: error.message } : { success: true }
  },

  async setDefaultPayoutMethod(id: string, providerId: string): Promise<{ success: boolean; error?: string }> {
    const { error: clearError } = await supabase
      .from('provider_payout_methods')
      .update({ is_default: false })
      .eq('provider_id', providerId)

    if (clearError) return { success: false, error: clearError.message }

    const { error } = await supabase
      .from('provider_payout_methods')
      .update({ is_default: true })
      .eq('id', id)
      .eq('provider_id', providerId)

    return error ? { success: false, error: error.message } : { success: true }
  },

  async deletePayoutMethod(id: string): Promise<{ success: boolean; error?: string }> {
    const { error } = await supabase.from('provider_payout_methods').delete().eq('id', id)
    return error ? { success: false, error: error.message } : { success: true }
  },

  // ── Wallet transactions ───────────────────────────────────────────────────

  async getEarningsSummary(
    providerId: string,
    startDate?: string,
    endDate?: string
  ): Promise<EarningsSummary> {
    let query = supabase
      .from('provider_transaction_logs')
      .select('amount, platform_fee_amount')
      .eq('provider_id', providerId)
      .eq('transaction_type', 'credit')
    if (startDate) query = (query as any).gte('created_at', startDate)
    if (endDate)   query = (query as any).lte('created_at', endDate)
    const { data } = await query
    const rows = (data ?? []) as { amount: number; platform_fee_amount: number }[]
    const netCredited  = rows.reduce((s, r) => s + Number(r.amount), 0)
    const platformFee  = rows.reduce((s, r) => s + Number(r.platform_fee_amount), 0)
    return {
      totalEarned:      netCredited + platformFee,
      platformFee,
      netCredited,
      transactionCount: rows.length,
    }
  },

  async getTransactionLogs(providerId: string, limit = 20): Promise<WalletTransaction[]> {
    const { data: logs } = await supabase
      .from('provider_transaction_logs')
      .select('id, transaction_type, amount, platform_fee_amount, related_withdrawal_id, description, created_at')
      .eq('provider_id', providerId)
      .order('created_at', { ascending: false })
      .limit(limit)

    const { data: withdrawals } = await supabase
      .from('provider_withdrawals')
      .select('id, amount, status, requested_at')
      .eq('provider_id', providerId)
      .order('requested_at', { ascending: false })
      .limit(limit)

    const transactions: WalletTransaction[] = ((logs ?? []) as any[]).map((log) => ({
      id: log.id as string,
      type: log.transaction_type === 'credit' ? 'earning' : 'withdrawal',
      amount: Number(log.amount),
      platformFee: Number(log.platform_fee_amount ?? 0),
      description: (log.description as string) || '',
      createdAt: (log.created_at as string) || '',
      status: undefined,
      withdrawalId: (log.related_withdrawal_id as string | null) ?? undefined,
    }))

    ;((withdrawals ?? []) as ProviderWithdrawalRow[]).forEach((withdrawal) => {
      const existingTransaction = transactions.find((transaction) => transaction.withdrawalId === withdrawal.id)

      if (existingTransaction) {
        existingTransaction.status = withdrawal.status ?? undefined
        return
      }

      transactions.push({
        id: withdrawal.id,
        type: 'withdrawal',
        amount: Number(withdrawal.amount ?? 0),
        platformFee: 0,
        description: `Withdrawal request - ${withdrawal.status ?? 'Pending'}`,
        status: withdrawal.status ?? undefined,
        createdAt: withdrawal.requested_at ?? '',
        withdrawalId: withdrawal.id,
      })
    })

    return transactions
      .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())
      .slice(0, limit)
  },

  async getAvailableBalance(providerId: string): Promise<number> {
    const wallet = await this.getWallet(providerId)
    if (!wallet) return 0
    return Math.max(0, Number((wallet as any).balance ?? 0))
  },

  async requestWithdrawal(
    providerId: string,
    amount: number,
    payoutMethodId: string
  ): Promise<{ success: boolean; error?: string; withdrawalId?: string; availableBalance?: number }> {
    try {
      const { data, error } = await (supabase.rpc as any)('request_provider_withdrawal', {
        provider_id_param: providerId,
        amount_param: amount,
        payout_method_id_param: payoutMethodId,
      })

      if (error) {
        return { success: false, error: error.message || 'Withdrawal request failed.' }
      }

      if (data && typeof data === 'object' && 'success' in data) {
        return {
          success: !!data.success,
          error: data.error ?? undefined,
          withdrawalId: data.withdrawalId ?? undefined,
          availableBalance: data.availableBalance ?? undefined,
        }
      }

      return { success: false, error: 'Unexpected response from withdrawal request.' }
    } catch (err: any) {
      return { success: false, error: err.message as string }
    }
  },

  // ── Static Documents ──────────────────────────────────────────────────────

  async getStaticDocument(title: string): Promise<{ title: string; content: string; updatedAt?: string } | null> {
    const { data } = await supabase
      .from('static_content')
      .select('title, content, updated_at')
      .eq('type', 'document')
      .eq('title', title)
      .order('updated_at', { ascending: false })
      .limit(1)
    const row = (data ?? [])[0]
    if (!row) return null
    return { title: (row as any).title, content: (row as any).content ?? '', updatedAt: (row as any).updated_at }
  },

  async getProviderTerms(): Promise<{ title: string; content: string; updatedAt?: string } | null> {
    return this.getStaticDocument('Provider Terms of Service')
  },

  async getProviderPrivacyPolicy(): Promise<{ title: string; content: string; updatedAt?: string } | null> {
    return this.getStaticDocument('Provider Privacy Policy')
  },

  async getProviderCodeOfConduct(): Promise<{ title: string; content: string; updatedAt?: string } | null> {
    return this.getStaticDocument('Provider Code of Conduct')
  },

  // ── Support ───────────────────────────────────────────────────────────────

  async getFAQs(category?: string): Promise<{ id: string; title: string; content: string }[]> {
    let query = supabase
      .from('static_content')
      .select('id, title, content')
      .eq('type', 'faq')
      .order('order_index', { ascending: true })
    if (category) query = (query as any).eq('category', category)
    const { data } = await query
    return (data ?? []) as { id: string; title: string; content: string }[]
  },

  async getAppConfig(): Promise<{ supportPhone: string; supportWhatsApp: string; supportEmail: string } | null> {
    const { data } = await supabase
      .from('app_config')
      .select('support_phone, support_whatsapp, support_email')
      .limit(1)
      .maybeSingle()
    if (!data) return null
    return {
      supportPhone:    (data as any).support_phone    ?? '+2349132371175',
      supportWhatsApp: (data as any).support_whatsapp ?? '+2349132371175',
      supportEmail:    (data as any).support_email    ?? 'hello@homicareplus.com',
    }
  },

  async submitSupportFeedback(params: {
    providerId: string
    subject: string
    message: string
    rating?: number
  }): Promise<{ success: boolean; error?: string }> {
    const { error } = await supabase.from('support_feedback').insert({
      user_role:   'provider',
      provider_id: params.providerId,
      subject:     params.subject || null,
      message:     params.message,
      rating:      params.rating ?? null,
    })
    return error ? { success: false, error: error.message } : { success: true }
  },

  // ── Service Rates ─────────────────────────────────────────────────────────

  async getServiceRates(providerId: string): Promise<Array<{
    id: string
    service_category_id: string
    service_name: string
    description?: string | null
    duration?: string | null
    min_rate?: number | null
    max_rate?: number | null
    base_rate?: number | null
    is_active: boolean
    has_rate: boolean
  }>> {
    try {
      const [catRes, rateRes] = await Promise.all([
        supabase.from('service_categories').select('*'),
        supabase.from('service_rates').select('*').eq('provider_id', providerId),
      ])
      const categories = (catRes.data ?? []) as any[]
      const rates      = (rateRes.data ?? []) as any[]

      return categories.map((cat) => {
        const providerRate = rates.find((r) => r.service_category_id === cat.id)
        return {
          id:                  providerRate?.id ?? `new-${cat.id}`,
          service_category_id: cat.id,
          service_name:        cat.title ?? cat.name ?? cat.type ?? 'Service',
          description:         cat.description ?? null,
          duration:            cat.duration ?? null,
          min_rate:            cat.min_rate ?? cat.base_price ?? null,
          max_rate:            cat.max_rate ?? cat.base_price ?? null,
          base_rate:           providerRate?.base_rate ?? cat.base_price ?? null,
          is_active:           providerRate?.is_active ?? false,
          has_rate:            Boolean(providerRate),
        }
      })
    } catch {
      return []
    }
  },

  async upsertServiceRate(
    providerId: string,
    serviceCategoryId: string,
    baseRate: number
  ): Promise<{ success: boolean; error?: string }> {
    const { error } = await (supabase as any)
      .from('service_rates')
      .upsert(
        { provider_id: providerId, service_category_id: serviceCategoryId, base_rate: baseRate, is_active: true },
        { onConflict: 'provider_id,service_category_id' }
      )
    return error ? { success: false, error: error.message } : { success: true }
  },

  async updateServiceRate(
    rateId: string,
    updates: { base_rate?: number; is_active?: boolean }
  ): Promise<{ success: boolean; error?: string }> {
    const { error } = await supabase.from('service_rates').update(updates).eq('id', rateId)
    return error ? { success: false, error: error.message } : { success: true }
  },

  // ── Appointment Detail ────────────────────────────────────────────────────

  async getProviderAppointmentById(appointmentId: string): Promise<ProviderAppointmentDetail | null> {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select(`
          id,
          conversation_id,
          provider_id,
          patient_id,
          service_type,
          duration,
          number_of_days,
          number_of_patients,
          urgency_level,
          status,
          scheduled_date,
          scheduled_time,
          location,
          notes,
          continuation_of_appointment_id,
          total_cost,
          created_at,
          patients (
            id,
            name,
            email,
            phone_number,
            profile_image_url,
            date_of_birth,
            gender
          )
        `)
        .eq('id', appointmentId)
        .single()
      if (error || !data) return null

      const [medicalRes, emergencyRes] = await Promise.all([
        supabase
          .from('appointment_medical_info')
          .select('current_symptoms, medical_history, allergies, current_medications, surgery_history, family_history, accident_record, blood_type, height_cm, weight_kg')
          .eq('appointment_id', appointmentId)
          .maybeSingle(),
        supabase
          .from('emergency_contacts')
          .select('id, name, relationship, phone_number')
          .eq('patient_id', (data as any).patient_id),
      ])

      const patient = (data as any).patients as any
      let patientAge: number | undefined
      if (patient?.date_of_birth) {
        const dob = new Date(patient.date_of_birth)
        const today = new Date()
        patientAge = today.getFullYear() - dob.getFullYear()
        const m = today.getMonth() - dob.getMonth()
        if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) patientAge--
      }

      const med = medicalRes.data as any
      const contacts = (emergencyRes.data ?? []) as any[]

      return {
        id:                          (data as any).id,
        conversationId:              (data as any).conversation_id ?? null,
        patientId:                   (data as any).patient_id,
        patientName:                 patient?.name ?? '',
        patientEmail:                patient?.email ?? undefined,
        patientPhone:                patient?.phone_number ?? '',
        patientImage:                patient?.profile_image_url ?? undefined,
        patientAge,
        patientGender:               patient?.gender ?? undefined,
        providerId:                  (data as any).provider_id,
        serviceType:                 (data as any).service_type,
        duration:                    (data as any).duration ?? 'single',
        numberOfDays:                (data as any).number_of_days ?? undefined,
        numberOfPatients:            (data as any).number_of_patients ?? undefined,
        urgencyLevel:                (data as any).urgency_level ?? undefined,
        status:                      (data as any).status ?? 'Requested',
        scheduledDate:               (data as any).scheduled_date ?? '',
        scheduledTime:               (data as any).scheduled_time ?? undefined,
        location:                    (data as any).location ?? '',
        notes:                       (data as any).notes ?? undefined,
        continuationOfAppointmentId: (data as any).continuation_of_appointment_id ?? undefined,
        isContinuation:              Boolean((data as any).continuation_of_appointment_id),
        totalCost:                   (data as any).total_cost ?? 0,
        createdAt:                   (data as any).created_at ?? '',
        medicalInfo: med ? {
          currentSymptoms:    med.current_symptoms    ?? undefined,
          medicalHistory:     med.medical_history     ?? undefined,
          allergies:          med.allergies           ?? undefined,
          currentMedications: med.current_medications ?? undefined,
          surgeryHistory:     med.surgery_history     ?? undefined,
          familyHistory:      med.family_history      ?? undefined,
          accidentRecord:     med.accident_record     ?? undefined,
          bloodType:          med.blood_type          ?? undefined,
          heightCm:           med.height_cm != null   ? Number(med.height_cm) : undefined,
          weightKg:           med.weight_kg != null   ? Number(med.weight_kg) : undefined,
        } : undefined,
        emergencyContacts: contacts.map((c) => ({
          id:           c.id,
          name:         c.name,
          relationship: c.relationship,
          phoneNumber:  c.phone_number,
        })),
      }
    } catch {
      return null
    }
  },
}
